/**
 * Sends browser errors to the server's error log (NFR-OPS). At most a few per page load, and
 * never anything that could fail loudly itself.
 */
declare const __APP_RELEASE__: string;

let sent = 0;
const seen = new Set<string>();

export function reportError(error: unknown): void {
  if (sent >= 5) return;
  const message =
    error instanceof Error ? `${error.name}: ${error.message}` : String(error ?? "Unknown error");
  // Lost connections are expected (the app works offline); they are not bugs.
  if (/Failed to fetch|NetworkError|network_error|Load failed|AbortError/i.test(message)) return;
  if (seen.has(message)) return;
  // Errors thrown by browser extensions' own code are not Zeeta's bugs.
  const stack = error instanceof Error ? (error.stack ?? "") : "";
  if (/(chrome|moz|safari-web|safari)-extension:\/\//.test(stack)) return;
  seen.add(message);
  sent += 1;
  try {
    const body = JSON.stringify({
      message: message.slice(0, 1000),
      stack: error instanceof Error ? (error.stack ?? null)?.slice(0, 4000) : null,
      path: window.location.pathname,
      release: typeof __APP_RELEASE__ === "string" ? __APP_RELEASE__ : null,
    });
    void fetch("/api/client-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      keepalive: true,
      body,
    }).catch(() => undefined);
  } catch {
    // Reporting must never throw.
  }
}

export function installErrorReporting(): void {
  window.addEventListener("error", (event) => reportError(event.error ?? event.message));
  window.addEventListener("unhandledrejection", (event) => reportError(event.reason));
}
